import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../../utils/authService';
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Button,
  Typography,
} from "@mui/material";
import { Spinner } from "react-bootstrap";
import * as XLSX from 'xlsx';
import { compareMaybeNumeric } from "../../../utils/numericSortUtils";

interface Project {
  name: string;
  id: number;
  batch_project_id: number;
}

interface FilterData {
  years: number[];
  colleges: Array<{ name: string; id: string }>;
  courses: Array<{ name: string; id: string }>;
  batches: Array<{ name: string; id: string }>;
  coordinators: Array<{ name: string; id: string }>;
  branches: Array<{ name: string; id: string }>;
  subjects: Array<{ name: string; id: string }>;
  projects: Project[];
  relations: {
    college_courses: { [key: string]: string[] };
    course_batches: { [key: string]: string[] };
    batch_subjects: { [key: string]: string[] };
    batch_projects: { [key: string]: number[] };
    college_branches: { [key: string]: string[] };
    coordinators_relations: { [key: string]: string[] };
  };
}

interface BatchData {
  batch_id: string;
  batch_name: string;
  progress_ahead: number;
  progress_ontime: number;
  progress_laggards: number;
  progress_total: number;
  live_session_full: number;
  live_session_acceptable: number;
  live_session_unacceptable: number;
  live_session_total: number;
  grade_sun: number;
  grade_moon: number;
  grade_star: number;
  grade_rest: number;
  grade_total: number;
}

interface CoordinatorResponse {
  coordinator_name: string;
  coordinator_id: string;
  coordinator_data: BatchData[];
}

interface CoordinatorData {
  coordinator_name: string;
  coordinator_id: string;
  progress_ahead: number;
  progress_ontime: number;
  progress_laggards: number;
  progress_total: number;
  live_session_full: number;
  live_session_acceptable: number;
  live_session_unacceptable: number;
  live_session_total: number;
  grade_sun: number;
  grade_moon: number;
  grade_star: number;
  grade_rest: number;
  grade_total: number;
}

type ReportData = CoordinatorResponse[];

const COLUMN_TYPES = {
  PROGRESS: 'progress',
  LIVE_SESSION: 'liveSession',
  GRADE: 'grade',
  DEFAULT: 'default',
} as const;

const COLUMN_BG_COLORS = {
  PROGRESS: '#e3f2fd',
  LIVE_SESSION: '#e8f5e9',
  GRADE: '#fff3e0',
  DEFAULT: '#f5f9fb',       
} as const;

/** Fixed column widths so group headers (colSpan) align with body cells on horizontal scroll. */
const REPORT_TABLE_COLUMN_COUNT = 12;
const REPORT_COLUMN_WIDTHS: readonly number[] = [
  110, 150, // coordinator, batch link
  100, 100, 100, // progress
  118, 118, 130, // live session
  72, 72, 72, 72, // grade
];
const REPORT_TABLE_WIDTH = REPORT_COLUMN_WIDTHS.reduce((sum, w) => sum + w, 0);

const SCROLLBAR_SX = {
  scrollbarWidth: "thin",
  scrollbarColor: "#bdbdbd transparent",
  "&::-webkit-scrollbar": {
    width: 8,
    height: 8,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#bdbdbd",
    borderRadius: 4,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
} as const;

/** Fills card width; horizontal scroll only when viewport is narrower than table min width. */
const TABLE_SCROLL_AREA_SX = {
  mt: 1,
  flex: 1,
  minHeight: 0,
  width: "100%",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
} as const;

const TABLE_SCROLL_SX = {
  width: "100%",
  maxHeight: "100%",
  height: "fit-content",
  flex: "0 1 auto",
  overflowX: "auto",
  overflowY: "auto",
  borderTop: "1px solid #000",
  borderLeft: "1px solid #000",
  boxSizing: "border-box",
  ...SCROLLBAR_SX,
} as const;

const TABLE_INNER_SX = {
  minWidth: REPORT_TABLE_WIDTH,
  width: "100%",
} as const;

const TABLE_HEADER_STICKY_SX = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  backgroundColor: COLUMN_BG_COLORS.DEFAULT,
} as const;

const REPORT_TABLE_SX = {
  width: "100%",
} as const;

const ReportColGroup: React.FC = () => (
  <colgroup>
    {REPORT_COLUMN_WIDTHS.map((width, index) => (
      <col
        key={index}
        style={{ width: `${(width / REPORT_TABLE_WIDTH) * 100}%` }}
      />
    ))}
  </colgroup>
);

const TABLE_BORDER_SX = {
  tableLayout: "fixed" as const,
  borderCollapse: "separate" as const,
  borderSpacing: 0,
  "& .MuiTableCell-root": {
    border: "none",
    boxShadow: "inset -1px 0 0 #000, inset 0 -1px 0 #000",
    backgroundClip: "padding-box",
    boxSizing: "border-box",
    fontSize: "12px",
    padding: "8px 12px",
    overflow: "hidden",
  },
  "& .MuiTableBody-root .MuiTableCell-root": {
    color: "#333",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    backgroundColor: COLUMN_BG_COLORS.DEFAULT,
  },
} as const;

const OverallProgressReport: React.FC = () => {
  const navigate = useNavigate();
  
  // Authentication check - redirect to login if not authenticated as Coordinator
  useEffect(() => {
    if (!sessionStorage.getItem("Coordinator")) {
      navigate('/');
      return;
    }
  }, [navigate]);

  const [filters, setFilters] = useState<FilterData>({
    years: [],
    colleges: [],
    courses: [],
    batches: [],
    coordinators: [],
    branches: [],
    subjects: [],
    projects: [],
    relations: {
      college_courses: {},
      course_batches: {},
      batch_subjects: {},
      batch_projects: {},
      college_branches: {},
      coordinators_relations: {}
    }
  });
  const [report, setReport] = useState<ReportData>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [dataError, setDataError] = useState<string>('');
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Filter states
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedCollege, setSelectedCollege] = useState<string>('');
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [selectedBatch, setSelectedBatch] = useState<string>('');
  const [selectedCoordinator, setSelectedCoordinator] = useState<string>('');
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [hasAutoApplied, setHasAutoApplied] = useState(false);
  const [shouldAutoApply, setShouldAutoApply] = useState(false);

  // Available options based on selections
  const [availableCourses, setAvailableCourses] = useState<Array<{ name: string; id: string }>>([]);
  const [availableBatches, setAvailableBatches] = useState<Array<{ name: string; id: string }>>([]);
  const [availableBranches, setAvailableBranches] = useState<Array<{ name: string; id: string }>>([]);
  const [availableSubjects, setAvailableSubjects] = useState<Array<{ name: string; id: string }>>([]);
  const [availableProjects, setAvailableProjects] = useState<Project[]>([]);
  const [availableCoordinators, setAvailableCoordinators] = useState<Array<{ name: string; id: string }>>([]);

  useEffect(() => {
    const loadFilters = async () => {
      setLoading(true);
      setError('');
      try {
        const userId = sessionStorage.getItem('Id');
        if (!userId) {
          setError('User ID not found. Please login again.');
          return;
        }
        
        const response = await authService.get(`batch-comparison/filters/${userId}`);
        setFilters(response.data);
        
         const overallFilters = sessionStorage.getItem('overallProgressFilters');
         const savedFilters =  overallFilters;
         
        if (savedFilters) {
           try {
             const parsedFilters = JSON.parse(savedFilters);
             const year = parsedFilters.year || '';
             const collegeId = parsedFilters.college_id || '';
             const courseId = parsedFilters.course_id || '';
             const batchId = parsedFilters.batch_id || '';
             const coordinatorId = parsedFilters.coordinator_id || '';
             const branchId = parsedFilters.branch_id || '';
             const subjectId = parsedFilters.subject_id || '';
             const projectId = parsedFilters.project_id;
             const batchProjectId = parsedFilters.batch_project_id;
             
             // Set all values at once to avoid interference from useEffect hooks
             if (year && response.data.years.includes(parseInt(year))) {
               setSelectedYear(year.toString());
             }
             
             if (collegeId && response.data.colleges.find((c: { id: string }) => c.id === collegeId)) {
               setSelectedCollege(collegeId);
               
               const courses = response.data.courses.filter((course: { name: string; id: string }) => 
                 response.data.relations.college_courses[collegeId]?.includes(course.id)
               );
               setAvailableCourses(courses);
               
               const branches = response.data.branches.filter((branch: { name: string; id: string }) => 
                 response.data.relations.college_branches[collegeId]?.includes(branch.id)
               );
               setAvailableBranches(branches);
               
               if (branchId && branches.find((b: { id: string }) => b.id === branchId)) {
                 setSelectedBranch(branchId);
               }
               
               if (courseId && courses.find((c: { id: string }) => c.id === courseId)) {
                 setSelectedCourse(courseId);
                 
                 const batches = response.data.batches.filter((batch: { name: string; id: string }) => 
                   response.data.relations.course_batches[courseId]?.includes(batch.id)
                 );
                 setAvailableBatches(batches);
                 
                 if (batchId && batches.find((b: { id: string }) => b.id === batchId)) {
                   setSelectedBatch(batchId);
                   
                   const subjects = response.data.subjects.filter((subject: { name: string; id: string }) => 
                     response.data.relations.batch_subjects[batchId]?.includes(subject.id)
                   );
                   setAvailableSubjects(subjects);
                   
                   const coordinatorNames = (response.data.relations.coordinators_relations[batchId] || []).map((name: string) => name.trim());
                   const coordinators = response.data.coordinators.filter((coordinator: { name: string; id: string }) => 
                     coordinatorNames.includes(coordinator.name.trim())
                   );
                   setAvailableCoordinators(coordinators);
                   
                   if (coordinatorId && coordinators.find((c: { name: string; id: string }) => c.id === coordinatorId)) {
                     setSelectedCoordinator(coordinatorId);
                   }
                   
                   if (subjectId && subjects.find((s: { id: string }) => s.id === subjectId)) {
                     setSelectedSubject(subjectId);
                   }
                   
                   const batchProjectIds = response.data.relations.batch_projects?.[batchId] || [];
                   const projects = (response.data.projects || []).filter((p: Project) => batchProjectIds.includes(p.batch_project_id));
                   setAvailableProjects(projects);
                   if (projectId != null && batchProjectId != null && projects.find((p: Project) => p.id === projectId && p.batch_project_id === batchProjectId)) {
                     setSelectedProject(`${projectId}_${batchProjectId}`);
                   }
                 }
               }
             }
             
            if (year && collegeId && courseId) {
              setShouldAutoApply(true);
            }
           } catch (error) {
             console.error('Error parsing saved filters:', error);
           }
         }
         
       } catch (err) {
         console.error('Error loading filters:', err);
         setError('Failed to load filter data. Please try again.');
       } finally {
         setLoading(false);
       }
     };

    loadFilters();
  }, []);

  // Load data when filters are applied
  const loadReportData = async () => {
    // Validate required fields
    if (!selectedYear || !selectedCollege || !selectedCourse) {
      const missingFields = [];
      if (!selectedYear) missingFields.push('Year');
      if (!selectedCollege) missingFields.push('College');
      if (!selectedCourse) missingFields.push('Course');
      setDataError(`Please select ${missingFields.join(', ')}`);
      setHasAppliedFilters(false);
      return;
    }

    setDataLoading(true);
    setDataError('');
    setHasAppliedFilters(true); // Set this immediately when loading starts
    
    try {
      const payload: Record<string, unknown> = {
        year: parseInt(selectedYear),
        college_id: selectedCollege,
        course_id: selectedCourse,
        batch_id: selectedBatch || "",
        coordinator_id: selectedCoordinator || "",
        branch_id: selectedBranch || "",
        subject_id: selectedSubject || ""
      };
      if (selectedProject) {
        const [project_id, batch_project_id] = selectedProject.split('_').map(Number);
        payload.project_id = project_id;
        payload.batch_project_id = batch_project_id;
      }

      const response = await authService.post('coordinator-comparison/data/', payload);
      setReport(response.data);
    } catch (err) {
      console.error('Error loading report data:', err);
      setDataError('Failed to load report data. Please try again.');
    } finally {
      setDataLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate('/coordinator/dashboard');
  };

  const handleDownloadClick = () => {
    if (!hasAppliedFilters || coordinatorEntries.length === 0) {
      alert('No data to download. Please apply filters first.');
      return;
    }

    // Prepare data for Excel export
    const excelData = [];
    
    // Add headers
    const headers = [
      'Coordinator',
      'Type',
      'Ahead',
      'Ontime', 
      'Laggards',
      'Full',
      'Acceptable',
      'Unacceptable',
      'Sun',
      'Moon',
      'Star',
      'Rest'
    ];
    excelData.push(headers);

    // Add data rows
    coordinatorEntries.forEach((data) => {
      // Number row
      const numberRow = [
        data.coordinator_name,
        'Number',
        data.progress_ahead,
        data.progress_ontime,
        data.progress_laggards,
        data.live_session_full,
        data.live_session_acceptable,
        data.live_session_unacceptable,
        data.grade_sun,
        data.grade_moon,
        data.grade_star,
        data.grade_rest
      ];
      excelData.push(numberRow);

      // Percentage row
      const percentageRow = [
        '',
        '%',
        calculatePercentage(data.progress_ahead, data.progress_total),
        calculatePercentage(data.progress_ontime, data.progress_total),
        calculatePercentage(data.progress_laggards, data.progress_total),
        calculatePercentage(data.live_session_full, data.live_session_total),
        calculatePercentage(data.live_session_acceptable, data.live_session_total),
        calculatePercentage(data.live_session_unacceptable, data.live_session_total),
        calculatePercentage(data.grade_sun, data.grade_total),
        calculatePercentage(data.grade_moon, data.grade_total),
        calculatePercentage(data.grade_star, data.grade_total),
        calculatePercentage(data.grade_rest, data.grade_total)
      ];
      excelData.push(percentageRow);
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Coordinator Report');

    // Generate filename with current date
    const currentDate = new Date().toISOString().split('T')[0];
    const filename = `coordinatorreport_${currentDate}.xlsx`;

    // Download the file
    XLSX.writeFile(wb, filename);
  };

  const handleCollegeChange = (collegeId: string) => {
    setSelectedCollege(collegeId);
    
    if (collegeId) {
      const courses = filters.courses.filter(course => 
        filters.relations.college_courses[collegeId]?.includes(course.id)
      );
      setAvailableCourses(courses);
      
      const branches = filters.branches.filter(branch => 
        filters.relations.college_branches[collegeId]?.includes(branch.id)
      );
      setAvailableBranches(branches);
    } else {
      setAvailableCourses([]);
      setAvailableBranches([]);
    }
    
    // Clear dependent selections
    setSelectedCourse('');
    setSelectedBatch('');
    setSelectedCoordinator('');
    setSelectedSubject('');
    setSelectedProject('');
    setAvailableBatches([]);
    setAvailableSubjects([]);
    setAvailableProjects([]);
    setAvailableCoordinators([]);
  };

  const handleCourseChange = (courseId: string) => {
    setSelectedCourse(courseId);
    
    if (courseId) {
      const batches = filters.batches.filter(batch => 
        filters.relations.course_batches[courseId]?.includes(batch.id)
      );
      setAvailableBatches(batches);
    } else {
      setAvailableBatches([]);
    }
    
    // Clear dependent selections
    setSelectedBatch('');
    setSelectedCoordinator('');
    setSelectedSubject('');
    setSelectedProject('');
    setAvailableSubjects([]);
    setAvailableProjects([]);
    setAvailableCoordinators([]);
  };

  const handleBatchChange = (batchId: string) => {
    setSelectedBatch(batchId);
    
    if (batchId) {
      const subjects = filters.subjects.filter(subject => 
        filters.relations.batch_subjects[batchId]?.includes(subject.id)
      );
      setAvailableSubjects(subjects);
      
      const batchProjectIds = filters.relations.batch_projects?.[batchId] || [];
      const projects = filters.projects.filter(p => batchProjectIds.includes(p.batch_project_id));
      setAvailableProjects(projects);
      
      const coordinatorNames = (filters.relations.coordinators_relations[batchId] || []).map(name => name.trim());
      const coordinators = filters.coordinators.filter(coordinator => 
        coordinatorNames.includes(coordinator.name.trim())
      );
      setAvailableCoordinators(coordinators);
    } else {
      setAvailableSubjects([]);
      setAvailableProjects([]);
      setAvailableCoordinators([]);
    }
    
    // Clear dependent selections
    setSelectedCoordinator('');
    setSelectedSubject('');
    setSelectedProject('');
  };

  const handleApplyFilters = () => {
    // Save current filters to sessionStorage before loading data
    const currentFilters: Record<string, unknown> = {
      year: selectedYear,
      college_id: selectedCollege,
      course_id: selectedCourse,
      batch_id: selectedBatch,
      coordinator_id: selectedCoordinator,
      branch_id: selectedBranch,
      subject_id: selectedSubject
    };
    if (selectedProject) {
      const [project_id, batch_project_id] = selectedProject.split('_').map(Number);
      currentFilters.project_id = project_id;
      currentFilters.batch_project_id = batch_project_id;
    }
    sessionStorage.setItem('overallProgressFilters', JSON.stringify(currentFilters));
    
    loadReportData();
    setHasAutoApplied(true);
  };

  // Auto-apply once on page load if mandatory filters already exist
  useEffect(() => {
    if (
      shouldAutoApply &&
      !hasAutoApplied &&
      selectedYear &&
      selectedCollege &&
      selectedCourse &&
      !loading &&
      !dataLoading
    ) {
      handleApplyFilters();
      setShouldAutoApply(false);
    }
  }, [shouldAutoApply, hasAutoApplied, selectedYear, selectedCollege, selectedCourse, loading, dataLoading]);

  const calculatePercentage = (value: number, total: number): string => {
    if (total === 0) return '0%';
    return Math.round((value / total) * 100).toString() + '%';
  };

  const getColumnBgColor = (columnType: 'progress' | 'liveSession' | 'grade' | 'default'): string => {
    switch (columnType) {
      case COLUMN_TYPES.PROGRESS:
        return COLUMN_BG_COLORS.PROGRESS;
      case COLUMN_TYPES.LIVE_SESSION:
        return COLUMN_BG_COLORS.LIVE_SESSION;
      case COLUMN_TYPES.GRADE:
        return COLUMN_BG_COLORS.GRADE;
      default:
        return COLUMN_BG_COLORS.DEFAULT;
    }
  };


  // Aggregate batch data for each coordinator
  const aggregateCoordinatorData = (coordinator: CoordinatorResponse): CoordinatorData => {
    const aggregated = coordinator.coordinator_data.reduce(
      (acc, batch) => ({
        progress_ahead: acc.progress_ahead + batch.progress_ahead,
        progress_ontime: acc.progress_ontime + batch.progress_ontime,
        progress_laggards: acc.progress_laggards + batch.progress_laggards,
        progress_total: acc.progress_total + batch.progress_total,
        live_session_full: acc.live_session_full + batch.live_session_full,
        live_session_acceptable: acc.live_session_acceptable + batch.live_session_acceptable,
        live_session_unacceptable: acc.live_session_unacceptable + batch.live_session_unacceptable,
        live_session_total: acc.live_session_total + batch.live_session_total,
        grade_sun: acc.grade_sun + batch.grade_sun,
        grade_moon: acc.grade_moon + batch.grade_moon,
        grade_star: acc.grade_star + batch.grade_star,
        grade_rest: acc.grade_rest + batch.grade_rest,
        grade_total: acc.grade_total + batch.grade_total,
      }),
      {
        progress_ahead: 0,
        progress_ontime: 0,
        progress_laggards: 0,
        progress_total: 0,
        live_session_full: 0,
        live_session_acceptable: 0,
        live_session_unacceptable: 0,
        live_session_total: 0,
        grade_sun: 0,
        grade_moon: 0,
        grade_star: 0,
        grade_rest: 0,
        grade_total: 0,
      }
    );

    return {
      coordinator_name: coordinator.coordinator_name,
      coordinator_id: coordinator.coordinator_id,
      ...aggregated,
    };
  };

  // Get aggregated coordinator data
  const getAggregatedCoordinators = (): CoordinatorData[] => {
    return report.map(coordinator => aggregateCoordinatorData(coordinator));
  };

  // Sorting functions
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedEntries = (): CoordinatorData[] => {
    const aggregated = getAggregatedCoordinators();
    
    if (!sortField) {
      // Always put Total at the bottom when no sort is applied
      return aggregated.sort((a, b) => {
        if (a.coordinator_id === "Total") return 1;
        if (b.coordinator_id === "Total") return -1;
        return 0;
      });
    }

    return aggregated.sort((a, b) => {
      // Always keep Total at the bottom
      if (a.coordinator_id === "Total") return 1;
      if (b.coordinator_id === "Total") return -1;

      let aValue: number | string;
      let bValue: number | string;

      switch (sortField) {
        case 'coordinator_name':
          aValue = a.coordinator_name;
          bValue = b.coordinator_name;
          break;
        case 'progress_ahead':
          aValue = a.progress_ahead;
          bValue = b.progress_ahead;
          break;
        case 'progress_ontime':
          aValue = a.progress_ontime;
          bValue = b.progress_ontime;
          break;
        case 'progress_laggards':
          aValue = a.progress_laggards;
          bValue = b.progress_laggards;
          break;
        case 'live_session_full':
          aValue = a.live_session_full;
          bValue = b.live_session_full;
          break;
        case 'live_session_acceptable':
          aValue = a.live_session_acceptable;
          bValue = b.live_session_acceptable;
          break;
        case 'live_session_unacceptable':
          aValue = a.live_session_unacceptable;
          bValue = b.live_session_unacceptable;
          break;
        case 'grade_sun':
          aValue = a.grade_sun;
          bValue = b.grade_sun;
          break;
        case 'grade_moon':
          aValue = a.grade_moon;
          bValue = b.grade_moon;
          break;
        case 'grade_star':
          aValue = a.grade_star;
          bValue = b.grade_star;
          break;
        case 'grade_rest':
          aValue = a.grade_rest;
          bValue = b.grade_rest;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return compareMaybeNumeric(aValue, bValue, sortDirection);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return compareMaybeNumeric(aValue, bValue, sortDirection);
      }

      return 0;
    });
  };

  const coordinatorEntries = getSortedEntries();

  // Get coordinator response data for batch details
  const getCoordinatorResponse = (coordinatorId: string): CoordinatorResponse | undefined => {
    return report.find(c => c.coordinator_id === coordinatorId);
  };

  return (
    <div className="container-fluid p-0 pe-2" style={{ marginTop: "12px" }}>
      <Card>
        <CardContent
          style={{
            height: `calc(100vh - 15px)`,
            overflow: "hidden",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
            style={{ flexShrink: 0 }}
          >
            <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Year
                </InputLabel>
                <Select
                  value={selectedYear}
                  onChange={(e: SelectChangeEvent<string>) => setSelectedYear(e.target.value)}
                  label="Year"
                  disabled={loading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Year
                  </MenuItem>
                  {filters.years.map(year => (
                    <MenuItem
                      key={year}
                      value={year.toString()}
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  College
                </InputLabel>
                 <Select
                   value={selectedCollege}
                   onChange={(e: SelectChangeEvent<string>) => handleCollegeChange(e.target.value)}
                   label="College"
                  disabled={loading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select College
                  </MenuItem>
                  {filters.colleges.map(college => (
                    <MenuItem
                      key={college.id}
                      value={college.id}
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      {college.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Course
                </InputLabel>
                 <Select
                   value={selectedCourse}
                   onChange={(e: SelectChangeEvent<string>) => handleCourseChange(e.target.value)}
                   label="Course"
                  disabled={!selectedCollege || loading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Course
                  </MenuItem>
                  {availableCourses.map(course => (
                    <MenuItem
                      key={course.id}
                      value={course.id}
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      {course.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Batch
                </InputLabel>
                 <Select
                   value={selectedBatch}
                   onChange={(e: SelectChangeEvent<string>) => handleBatchChange(e.target.value)}
                   label="Batch"
                  disabled={!selectedCourse || loading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Batch
                  </MenuItem>
                  {availableBatches.map(batch => (
                    <MenuItem
                      key={batch.id}
                      value={batch.id}
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      {batch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Coordinator
                </InputLabel>
                <Select
                  value={selectedCoordinator}
                  onChange={(e: SelectChangeEvent<string>) => setSelectedCoordinator(e.target.value)}
                  label="Coordinator"
                  disabled={!selectedBatch || loading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Coordinator
                  </MenuItem>
                  {availableCoordinators.map(coordinator => (
                    <MenuItem
                      key={coordinator.id}
                      value={coordinator.id}
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      {coordinator.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Subject
                </InputLabel>
                <Select
                  value={selectedSubject}
                  onChange={(e: SelectChangeEvent<string>) => {
                    setSelectedSubject(e.target.value);
                    if (e.target.value) setSelectedProject('');
                  }}
                  label="Subject"
                  disabled={!selectedBatch || loading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Subject
                  </MenuItem>
                  {availableSubjects.map(subject => (
                    <MenuItem
                      key={subject.id}
                      value={subject.id}
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      {subject.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography sx={{ alignSelf: "center", fontSize: "14px", px: 0.5 }}>OR</Typography>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 180 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Project
                </InputLabel>
                <Select
                  value={selectedProject}
                  onChange={(e: SelectChangeEvent<string>) => {
                    setSelectedProject(e.target.value);
                    if (e.target.value) setSelectedSubject('');
                  }}
                  label="Project"
                  disabled={!selectedBatch || loading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Project
                  </MenuItem>
                  {availableProjects.map(project => (
                    <MenuItem
                      key={project.batch_project_id}
                      value={`${project.id}_${project.batch_project_id}`}
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyFilters}
                disabled={loading || dataLoading}
                style={{
                  height: "36px",
                  fontSize: "12px",
                  textTransform: "none",
                }}
              >
                {dataLoading ? 'Loading...' : 'Apply'}
              </Button>
            </div>
          </Box>

          <Box sx={TABLE_SCROLL_AREA_SX}>
            <Box sx={TABLE_SCROLL_SX}>
            <Box sx={TABLE_INNER_SX}>
            <Box sx={TABLE_HEADER_STICKY_SX}>
              <Table size="small" sx={{ ...TABLE_BORDER_SX, ...REPORT_TABLE_SX }}>
                <ReportColGroup />
                <TableHead>
                <TableRow>
                  <TableCell 
                    colSpan={2} 
                    rowSpan={2} 
                    align="center" 
                    style={{
                      backgroundColor: getColumnBgColor(COLUMN_TYPES.DEFAULT),
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort('coordinator_name')}
                  >
                    Coordinator
                  </TableCell>
                  <TableCell 
                    colSpan={3} 
                    align="center" 
                    style={{
                      backgroundColor: getColumnBgColor(COLUMN_TYPES.DEFAULT),
                      fontWeight: 600,
                    }}
                  >
                    Progress
                  </TableCell>
                  <TableCell 
                    colSpan={3} 
                    align="center" 
                    style={{
                      backgroundColor: getColumnBgColor(COLUMN_TYPES.DEFAULT),
                      fontWeight: 600,
                    }}
                  >
                    Live Session
                  </TableCell>
                  <TableCell 
                    colSpan={4} 
                    align="center" 
                    style={{
                      backgroundColor: getColumnBgColor(COLUMN_TYPES.DEFAULT),
                      fontWeight: 600,
                    }}
                  >
                    Grade
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell 
                    align="center" 
                    onClick={() => handleSort('progress_ahead')}
                    style={{
                      backgroundColor: getColumnBgColor(COLUMN_TYPES.DEFAULT),
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Ahead
                  </TableCell>
                  <TableCell 
                    align="center" 
                    onClick={() => handleSort('progress_ontime')}
                    style={{
                      backgroundColor: getColumnBgColor(COLUMN_TYPES.DEFAULT),
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Ontime
                  </TableCell>
                  <TableCell 
                    align="center" 
                    onClick={() => handleSort('progress_laggards')}
                    style={{
                      backgroundColor: getColumnBgColor(COLUMN_TYPES.DEFAULT),
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Laggards
                  </TableCell>
                  <TableCell 
                    align="center" 
                    onClick={() => handleSort('live_session_full')}
                    style={{
                      backgroundColor: getColumnBgColor(COLUMN_TYPES.DEFAULT),
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Full (100%)
                  </TableCell>
                  <TableCell 
                    align="center" 
                    onClick={() => handleSort('live_session_acceptable')}
                    style={{
                      backgroundColor: getColumnBgColor(COLUMN_TYPES.DEFAULT),
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Acceptable (&gt; 90%)
                  </TableCell>
                  <TableCell 
                    align="center" 
                    onClick={() => handleSort('live_session_unacceptable')}
                    style={{
                      backgroundColor: getColumnBgColor(COLUMN_TYPES.DEFAULT),
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Unacceptable (&lt; 90%)
                  </TableCell>
                  <TableCell 
                    align="center" 
                    onClick={() => handleSort('grade_sun')}
                    style={{
                      backgroundColor: getColumnBgColor(COLUMN_TYPES.DEFAULT),
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Sun
                  </TableCell>
                  <TableCell 
                    align="center" 
                    onClick={() => handleSort('grade_moon')}
                    style={{
                      backgroundColor: getColumnBgColor(COLUMN_TYPES.DEFAULT),
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Moon
                  </TableCell>
                  <TableCell 
                    align="center" 
                    onClick={() => handleSort('grade_star')}
                    style={{
                      backgroundColor: getColumnBgColor(COLUMN_TYPES.DEFAULT),
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Star
                  </TableCell>
                  <TableCell 
                    align="center" 
                    onClick={() => handleSort('grade_rest')}
                    style={{
                      backgroundColor: getColumnBgColor(COLUMN_TYPES.DEFAULT),
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Rest
                  </TableCell>
                </TableRow>
                </TableHead>
              </Table>
            </Box>
            <Table size="small" sx={{ ...TABLE_BORDER_SX, ...REPORT_TABLE_SX }}>
              <ReportColGroup />
              <TableBody>
                {loading || dataLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={REPORT_TABLE_COLUMN_COUNT}
                      align="center"
                      style={{ padding: "20px" }}
                    >
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </TableCell>
                  </TableRow>
                ) : !hasAppliedFilters ? (
                  <TableRow>
                    <TableCell
                      colSpan={REPORT_TABLE_COLUMN_COUNT}
                      align="center"
                      style={{
                        padding: "20px",
                        color: "#666",
                        fontSize: "14px",
                        fontStyle: "italic",
                      }}
                    >
                      {error ||dataError || "Please select filters and click Apply to view data"}
                    </TableCell>
                  </TableRow>
                ) : coordinatorEntries.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={REPORT_TABLE_COLUMN_COUNT}
                      align="center"
                      style={{
                        padding: "20px",
                        color: "#666",
                        fontSize: "14px",
                        fontStyle: "italic",
                      }}
                    >
                      No data available for the selected criteria
                    </TableCell>
                  </TableRow>
                ) : (
                  coordinatorEntries.map((data, index) => {
                    const coordinatorResponse = getCoordinatorResponse(data.coordinator_id);
                    const batches = coordinatorResponse?.coordinator_data || [];
                    const isTotal = data.coordinator_id === "Total";
                    // Filter out Total batches for individual coordinators
                    const actualBatches = isTotal ? [] : batches.filter(b => b.batch_id !== "Total" && b.batch_name !== "Total");
                    const hasBatches = actualBatches.length > 0;
                    // For Total coordinator: 2 rows (Number and %), for individual coordinators: only batch rows
                    const rowSpan = isTotal ? 2 : (hasBatches ? actualBatches.length * 2 : 0);
                    const cellBgColor = isTotal? 'parent':getColumnBgColor(COLUMN_TYPES.DEFAULT);
                    
                    return (
                      <React.Fragment key={data.coordinator_id}>
                        {isTotal ? (
                          <>
                            <TableRow>
                              <TableCell 
                                rowSpan={2}
                                align="center"
                                style={{ 
                                  cursor: "default",
                                  verticalAlign: "middle",
                                  backgroundColor: cellBgColor,
                                }}
                              >
                                <span style={{ 
                                  color: "#333", 
                                  textDecoration: "none" 
                                }}>
                                  {data.coordinator_name}
                                </span>
                              </TableCell>
                              <TableCell 
                                align="center"
                                style={{ 
                                  verticalAlign: "middle",
                                  backgroundColor: cellBgColor,
                                }}
                              >
                              </TableCell>
                              <TableCell 
                                align="center"
                                style={{ 
                                  backgroundColor: getColumnBgColor(COLUMN_TYPES.PROGRESS),
                                }}
                              >
                                {data.progress_ahead}
                              </TableCell>
                              <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.PROGRESS) }}>{data.progress_ontime}</TableCell>
                              <TableCell 
                                align="center" 
                                style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.PROGRESS) }}
                                className={data.progress_laggards > 0 ? 'text-danger' : ''}
                              >
                                {data.progress_laggards}
                              </TableCell>
                              <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.LIVE_SESSION) }}>{data.live_session_full}</TableCell>
                              <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.LIVE_SESSION) }}>{data.live_session_acceptable}</TableCell>
                              <TableCell 
                                align="center" 
                                style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.LIVE_SESSION) }}
                                className={data.live_session_unacceptable > 0 ? 'text-danger' : ''}
                              >
                                {data.live_session_unacceptable}
                              </TableCell>
                              <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.GRADE) }}>{data.grade_sun}</TableCell>
                              <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.GRADE) }}>{data.grade_moon}</TableCell>
                              <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.GRADE) }}>{data.grade_star}</TableCell>
                              <TableCell 
                                align="center" 
                                style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.GRADE) }}
                                className={data.grade_rest > 0 ? 'text-danger' : ''}
                              >
                                {data.grade_rest}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell 
                                align="center"
                                style={{ 
                                  verticalAlign: "middle",
                                  backgroundColor: cellBgColor,
                                }}
                              >
                              </TableCell>
                              <TableCell 
                                align="center"
                                style={{ 
                                  backgroundColor: getColumnBgColor(COLUMN_TYPES.PROGRESS),
                                }}
                              >
                                {calculatePercentage(data.progress_ahead, data.progress_total)}
                              </TableCell>
                              <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.PROGRESS) }}>{calculatePercentage(data.progress_ontime, data.progress_total)}</TableCell>
                              <TableCell 
                                align="center" 
                                style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.PROGRESS) }}
                                className={data.progress_laggards > 0 ? 'text-danger' : ''}
                              >
                                {calculatePercentage(data.progress_laggards, data.progress_total)}
                              </TableCell>
                              <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.LIVE_SESSION) }}>{calculatePercentage(data.live_session_full, data.live_session_total)}</TableCell>
                              <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.LIVE_SESSION) }}>{calculatePercentage(data.live_session_acceptable, data.live_session_total)}</TableCell>
                              <TableCell 
                                align="center" 
                                style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.LIVE_SESSION) }}
                                className={data.live_session_unacceptable > 0 ? 'text-danger' : ''}
                              >
                                {calculatePercentage(data.live_session_unacceptable, data.live_session_total)}
                              </TableCell>
                              <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.GRADE) }}>{calculatePercentage(data.grade_sun, data.grade_total)}</TableCell>
                              <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.GRADE) }}>{calculatePercentage(data.grade_moon, data.grade_total)}</TableCell>
                              <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.GRADE) }}>{calculatePercentage(data.grade_star, data.grade_total)}</TableCell>
                              <TableCell 
                                align="center" 
                                style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.GRADE) }}
                                className={data.grade_rest > 0 ? 'text-danger' : ''}
                              >
                                {calculatePercentage(data.grade_rest, data.grade_total)}
                              </TableCell>
                            </TableRow>
                          </>
                        ) : hasBatches ? (
                          actualBatches.map((batch, batchIndex) => (
                            <React.Fragment key={`${data.coordinator_id}-${batch.batch_id}-${batchIndex}`}>
                              <TableRow>
                                {batchIndex === 0 && (
                                  <TableCell 
                                    rowSpan={actualBatches.length * 2}
                                    align="center"
                                    style={{ 
                                      fontWeight: 500,
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    {data.coordinator_name}
                                  </TableCell>
                                )}
                                <TableCell 
                                  rowSpan={2}
                                  align="center"
                                  style={{ 
                                    verticalAlign: "middle",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    const [project_id, batch_project_id] = selectedProject ? selectedProject.split('_').map(Number) : [null, null];
                                    const payload = {
                                      year: parseInt(selectedYear),
                                      college_id: selectedCollege,
                                      course_id: selectedCourse,
                                      batch_id: batch.batch_id,
                                      coordinator_id: data.coordinator_id,
                                      subject_id: selectedSubject || "",
                                      ...(project_id != null && batch_project_id != null && { project_id, batch_project_id })
                                    };
                                    navigate('/coordinator/sub-batch-details', {
                                      state: {
                                        payload: payload,
                                        coordinatorId: data.coordinator_id,
                                        coordinatorName: data.coordinator_name,
                                        batchId: batch.batch_id,
                                        batchName: batch.batch_name || batch.batch_id
                                      }
                                    });
                                  }}
                                >
                                  <span style={{ 
                                    color: "#1976d2", 
                                    textDecoration: "underline" 
                                  }}>
                                    {batch.batch_name || batch.batch_id} ({batch.progress_total})
                                  </span>
                                </TableCell>
                                <TableCell 
                                  align="center"
                                  style={{ 
                                    backgroundColor: getColumnBgColor(COLUMN_TYPES.PROGRESS),
                                  }}
                                >
                                  {batch.progress_ahead}
                                </TableCell>
                                <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.PROGRESS) }}>{batch.progress_ontime}</TableCell>
                                <TableCell 
                                  align="center" 
                                  style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.PROGRESS) }}
                                  className={batch.progress_laggards > 0 ? 'text-danger' : ''}
                                >
                                  {batch.progress_laggards}
                                </TableCell>
                                <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.LIVE_SESSION) }}>{batch.live_session_full}</TableCell>
                                <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.LIVE_SESSION) }}>{batch.live_session_acceptable}</TableCell>
                                <TableCell 
                                  align="center" 
                                  style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.LIVE_SESSION) }}
                                  className={batch.live_session_unacceptable > 0 ? 'text-danger' : ''}
                                >
                                  {batch.live_session_unacceptable}
                                </TableCell>
                                <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.GRADE) }}>{batch.grade_sun}</TableCell>
                                <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.GRADE) }}>{batch.grade_moon}</TableCell>
                                <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.GRADE) }}>{batch.grade_star}</TableCell>
                                <TableCell 
                                  align="center" 
                                  style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.GRADE) }}
                                  className={batch.grade_rest > 0 ? 'text-danger' : ''}
                                >
                                  {batch.grade_rest}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell 
                                  align="center"
                                  style={{ 
                                    backgroundColor: getColumnBgColor(COLUMN_TYPES.PROGRESS),
                                  }}
                                >
                                  {calculatePercentage(batch.progress_ahead, batch.progress_total)}
                                </TableCell>
                                <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.PROGRESS) }}>{calculatePercentage(batch.progress_ontime, batch.progress_total)}</TableCell>
                                <TableCell 
                                  align="center" 
                                  style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.PROGRESS) }}
                                  className={batch.progress_laggards > 0 ? 'text-danger' : ''}
                                >
                                  {calculatePercentage(batch.progress_laggards, batch.progress_total)}
                                </TableCell>
                                <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.LIVE_SESSION) }}>{calculatePercentage(batch.live_session_full, batch.live_session_total)}</TableCell>
                                <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.LIVE_SESSION) }}>{calculatePercentage(batch.live_session_acceptable, batch.live_session_total)}</TableCell>
                                <TableCell 
                                  align="center" 
                                  style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.LIVE_SESSION) }}
                                  className={batch.live_session_unacceptable > 0 ? 'text-danger' : ''}
                                >
                                  {calculatePercentage(batch.live_session_unacceptable, batch.live_session_total)}
                                </TableCell>
                                <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.GRADE) }}>{calculatePercentage(batch.grade_sun, batch.grade_total)}</TableCell>
                                <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.GRADE) }}>{calculatePercentage(batch.grade_moon, batch.grade_total)}</TableCell>
                                <TableCell align="center" style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.GRADE) }}>{calculatePercentage(batch.grade_star, batch.grade_total)}</TableCell>
                                <TableCell 
                                  align="center" 
                                  style={{ backgroundColor: getColumnBgColor(COLUMN_TYPES.GRADE) }}
                                  className={batch.grade_rest > 0 ? 'text-danger' : ''}
                                >
                                  {calculatePercentage(batch.grade_rest, batch.grade_total)}
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          ))
                        ) : null}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
            </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default OverallProgressReport;
  
  